import { captureException, addBreadcrumb } from '@/utils/sentry'
import { z } from 'zod'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const API_TIMEOUT = 30000

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`${status} ${statusText}`)
    this.name = 'ApiError'
  }
}

export class OfflineError extends Error {
  constructor() {
    super('No internet connection')
    this.name = 'OfflineError'
  }
}

// Request configuration type
interface RequestConfig extends RequestInit {
  timeout?: number
  params?: Record<string, string | number | boolean | undefined>
  skipSentryLogging?: boolean | number[]
  responseType?: 'json' | 'blob'
}

// Response type with generic data type
interface ApiResponse<T> {
  data: T
  status: number
  statusText: string
  headers: Headers
}

// Base API class with common HTTP methods
export class BaseApi {
  private static instance: BaseApi
  private baseUrl: string
  private defaultHeaders: HeadersInit
  private tokenGetter?: (forceRefresh: boolean) => Promise<string | null>
  private tokenHeader: string | null = null
  private extraHeaders: Record<string, string> = {}
  private readOnly = false
  private lastOfflineLogTime = 0
  private offlineLogThrottle = 30000

  private constructor() {
    this.baseUrl = API_BASE_URL
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  public static getInstance(): BaseApi {
    if (!BaseApi.instance) {
      BaseApi.instance = new BaseApi()
    }
    return BaseApi.instance
  }

  // Set a function that will be called to get a fresh token before each request
  public setTokenGetter(getter: (forceRefresh: boolean) => Promise<string | null>): void {
    this.tokenGetter = getter
  }

  // Send the token in this header instead of `Authorization: Bearer` (the Hub preview's token)
  public setTokenHeader(header: string | null): void {
    this.tokenHeader = header
  }

  // Headers sent with every request, e.g. the locations picked in the Hub preview
  public setExtraHeaders(headers: Record<string, string>): void {
    this.extraHeaders = headers
  }

  // Refuse writes without calling the server (the Hub's read-only preview)
  public setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly
  }

  // Helper method to get current auth token
  private async getAuthToken(forceRefresh: boolean): Promise<string | null> {
    if (this.tokenGetter) {
      return await this.tokenGetter(forceRefresh)
    }
    return null
  }

  // Helper method to build URL with query parameters
  private buildQueryString(params?: Record<string, string | number | boolean | undefined>): string {
    if (!params) return ''

    const queryParams = Object.entries(params)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&')

    return queryParams ? `?${queryParams}` : ''
  }

  // Helper method to handle response
  private async handleResponse<T>(response: Response, responseType: 'json' | 'blob' = 'json'): Promise<ApiResponse<T>> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new ApiError(response.status, response.statusText, errorData)
    }

    let data;

    try {
      data = responseType === 'blob' ? await response.blob() : await response.json()
    } catch (error) {}

    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    }
  }

  // Helper method to handle request timeout
  private async withTimeout<T>(
    promise: Promise<T>,
    timeout: number
  ): Promise<T> {
    const timeoutPromise = new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new ApiError(408, 'Request timeout'))
      }, timeout)
    })

    return Promise.race([promise, timeoutPromise])
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {},
    isFormData: boolean = false,
    isRetry: boolean = false,
    isNetworkRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    if (!navigator.onLine) {
      const offlineError = new OfflineError();
      addBreadcrumb('api-request', 'Request blocked: device offline', {
        endpoint,
        method: config.method || 'GET',
      });
      
      const now = Date.now();
      if (now - this.lastOfflineLogTime > this.offlineLogThrottle) {
        this.lastOfflineLogTime = now;
        captureException(offlineError, {
          extra: {
            endpoint,
            method: config.method || 'GET',
            offline: true,
            note: 'Throttled: only logging once per 30s',
          },
        });
      }
      
      throw offlineError;
    }

    const {
      timeout = API_TIMEOUT,
      params,
      headers = {},
      skipSentryLogging,
      responseType,
      ...restConfig
    } = config

    const method = (restConfig.method || 'GET').toUpperCase()
    if (this.readOnly && method !== 'GET' && method !== 'HEAD') {
      throw new ApiError(403, 'Read-only preview')
    }

    const queryString = this.buildQueryString(params)
    const url = `${this.baseUrl}${endpoint}${queryString}`
    
    const token = await this.getAuthToken(isRetry)
    const tokenHeaders: Record<string, string> = !token
      ? {}
      : this.tokenHeader
        ? { [this.tokenHeader]: token }
        : { Authorization: `Bearer ${token}` }
    
    const mergedHeaders: HeadersInit = {
      ...this.defaultHeaders,
      ...tokenHeaders,
      ...this.extraHeaders,
      ...headers,
    }

    if (isFormData) {
      delete (mergedHeaders as Record<string, string>)['Content-Type'];
    }

    try {
      const requestPromise = fetch(url, {
        ...restConfig,
        headers: mergedHeaders,
      }).then(response => this.handleResponse<T>(response, responseType))

      return await this.withTimeout(requestPromise, timeout)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && !isRetry && this.tokenGetter) {                  
        return this.request<T>(endpoint, config, isFormData, true)
      }

      // Retry once on browser-level network failures (TypeError) before logging.
      // Covers Safari "Load failed" and Chrome "Failed to fetch" caused by transient
      // connectivity drops — particularly common in WKWebView on iOS.
      // Uses isNetworkRetry (not isRetry) so the token is NOT force-refreshed —
      // a network failure doesn't imply the token is stale.
      if (error instanceof TypeError && !isNetworkRetry) {
        return this.request<T>(endpoint, config, isFormData, false, true)
      }
      
      // Determine if we should skip Sentry logging based on configuration
      let shouldSkipLogging = false;
      
      if (skipSentryLogging !== undefined && error instanceof ApiError) {
        if (typeof skipSentryLogging === 'boolean') {
          // If boolean, skip all errors if true
          shouldSkipLogging = skipSentryLogging;
        } else if (Array.isArray(skipSentryLogging)) {
          // If array of status codes, skip if error status matches
          shouldSkipLogging = skipSentryLogging.includes(error.status);
        }
      }
      
      if (!shouldSkipLogging) {
        captureException(error as Error, {
          extra: {
            endpoint,
            config,
            isFormData,
            isRetry,
            online: navigator.onLine,
          },
        });
      }
      
      throw error
    }
  }

  // HTTP methods
  public async get<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'GET' })
  }

  public async post<T>(
    endpoint: string,
    data?: unknown,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  public async put<T>(
    endpoint: string,
    data?: unknown,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  public async delete<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' })
  }

  public async patch<T>(
    endpoint: string,
    data?: unknown,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  /**
   * Post method specifically for FormData objects
   * Does not stringify the body as FormData needs to be sent as-is
   */
  public async postFormData<T>(
    endpoint: string,
    formData: FormData,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    // Get all headers except Content-Type from defaultHeaders  
    
    return this.request<T>(
      endpoint,
      {
        ...config,
        method: "POST",
        body: formData,
      },
      true
    );
  }
}

// Export singleton instance
export const api = BaseApi.getInstance()

// Example usage with Zod schema validation
export async function createValidatedRequest<T>(
  schema: z.ZodType<T>,
  request: Promise<ApiResponse<unknown>>
): Promise<ApiResponse<T>> {
  const response = await request
  const validatedData = schema.parse(response.data)
  return {
    ...response,
    data: validatedData,
  }
} 